import { IAppState } from "../../state/Store";
import Datastore from "../../communication/Datastore";
import Recommendation from "../../types/admin/Recommendation";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

// Define the type for our state store entry
type RecommendationsState = {
    recommendations: Recommendation[];
    status: string;
    age?: Date;
    error: string;
};

// Define the initial state for our state store entry
const initialState: RecommendationsState = {
    recommendations: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const recommendationsSlice = createSlice({
    name: "recommendations",
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    recommendations:
                        rawData?.payload?.recommendations?.recommendations ??
                        [],
                    status: !rawData?.payload?.recommendations?.recommendations
                        ?.length
                        ? "idle"
                        : "succeeded",
                    age: rawData?.payload?.recommendations?.age?.toDate(),
                    error: null,
                };
            })
            .addCase(fetchRecommendations.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchRecommendations.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.age = new Date();

                for (const recommendation of action.payload) {
                    switch (recommendation.severity) {
                        case "Low":
                            recommendation.icon = "lightbulb";
                            recommendation.iconColor = "";
                            break;
                        case "Normal":
                            recommendation.icon = "info";
                            recommendation.iconColor = "";
                            break;
                        case "High":
                            recommendation.icon = "warning";
                            recommendation.iconColor = "yellow";
                            break;
                        case "Extreme":
                            recommendation.icon = "error";
                            recommendation.iconColor = "red";
                            break;
                    }
                }

                state.recommendations = action.payload;
            })
            .addCase(fetchRecommendations.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

// This is a thunk, they are used to load the state
const fetchRecommendations = createAsyncThunk(
    "recommendations/fetchRecommendations",
    async () => {
        return await Datastore.getType<Recommendation[]>("api/Recommendations");
    },
    {
        condition: (_, { getState }) => {
            const { recommendations } = (getState() as IAppState).db;
            return (
                recommendations.status !== "loading" &&
                (!recommendations.age ||
                    recommendations.age.addMinutes(5) < new Date())
            );
        },
    },
);

export const initialiseRecommendations = createAsyncThunk(
    "recommendations/initialiseRecommendations",
    async (_, thunkAPI) => {
        await thunkAPI.dispatch(fetchRecommendations());
    },
);

// We have to export the reducer as this is added to the state store
export default recommendationsSlice.reducer;
